/**
 * Radio-group of stage colours. Each swatch is a 40 px target around a 26 px
 * disc. Left/Right move the selection locally and the PATCH is sent once the
 * keys stop for 350 ms (QA stage 1: one request per key press was too many);
 * a click commits immediately. Up/Down are left to a parent Menu.
 *
 * Inside a `role="menu"` the APG wants `group` + `menuitemradio`; elsewhere
 * (the settings page) it is a plain `radiogroup` + `radio`.
 */
import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { STAGE_COLORS } from './stage-colors';

export const COALESCE_MS = 350;

export function ColorSwatches({
  value,
  onChange,
  label = 'Stage colour',
  inMenu = false,
}: {
  value: string | null;
  onChange: (hex: string) => void;
  label?: string;
  inMenu?: boolean;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const current = (pending ?? value ?? '').toLowerCase();

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  // The server echoed the colour back: drop the local override.
  useEffect(() => { if (pending && value?.toLowerCase() === pending.toLowerCase()) setPending(null); }, [value, pending]);

  const commit = (hex: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    setPending(hex);
    onChange(hex);
  };
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const idx = STAGE_COLORS.findIndex((c) => c.hex.toLowerCase() === current);
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = STAGE_COLORS[(idx + step + STAGE_COLORS.length) % STAGE_COLORS.length]!;
    setPending(next.hex);
    e.currentTarget.querySelector<HTMLElement>(`[data-hex="${next.hex}"]`)?.focus();
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { timer.current = null; onChange(next.hex); }, COALESCE_MS);
  };

  return (
    <div role={inMenu ? 'group' : 'radiogroup'} aria-label={label} className="-mx-1 flex flex-wrap" onKeyDown={onKey}>
      {STAGE_COLORS.map((c) => {
        const selected = c.hex.toLowerCase() === current;
        return (
          <button
            key={c.hex}
            type="button"
            role={inMenu ? 'menuitemradio' : 'radio'}
            aria-checked={selected}
            aria-label={c.name}
            title={c.name}
            data-hex={c.hex}
            tabIndex={selected || (!current && c === STAGE_COLORS[0]) ? 0 : -1}
            onClick={() => commit(c.hex)}
            className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-page"
          >
            <span
              style={{ backgroundColor: c.hex }}
              className={`flex h-[26px] w-[26px] items-center justify-center rounded-full ring-offset-2 ${
                selected ? 'ring-2 ring-navy' : ''
              }`}
            >
              {selected && <Check size={13} strokeWidth={3} className="text-white" aria-hidden />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
